// The console's Settings › Mail screen (console-mail-settings WEB-ML-01 to -06, VisionPlatform notifications-email
// ML-90 to ML-94, P-98) over vision-api's getMailSettings, putMailSettings, testMailSettings and checkBounceMailbox.
import type { components } from "../../../api/generated/vision-api";
import { apiClient, unwrap } from "../../../api/client";
import { ApiError } from "../../../api/problem";

export type MailSettings = components["schemas"]["MailSettings"];
export type MailSettingsInput = components["schemas"]["MailSettingsInput"];
export type MailTestResult = components["schemas"]["MailTestResult"];
type GrantLike = { capability: string };

/** A password field: left as it is (keep the stored one), a new one typed, or cleared (ML-91). */
export type PasswordEdit = { kind: "keep" } | { kind: "set"; value: string } | { kind: "clear" };

/** The form's fields, all as typed; numbers stay text until they are sent. */
export type MailForm = {
  smtp_host: string;
  smtp_port: string;
  smtp_security: MailSettingsInput["smtp_security"];
  smtp_user: string;
  smtp_password: PasswordEdit;
  from_name: string;
  from_address: string;
  reply_to: string;
  envelope_from: string;
  sales_address: string;
  bounce_imap_host: string;
  bounce_imap_port: string;
  bounce_imap_user: string;
  bounce_imap_password: PasswordEdit;
};

/** Saving, testing and checking need `staff.manage` (the API's route table); the screen is hidden without it. */
export function canManageMail(grants: readonly GrantLike[] | undefined): boolean {
  return (grants ?? []).some((g) => g.capability === "staff.manage");
}

/** The form filled from the settings in force; passwords start as "keep". */
export function formFrom(s: MailSettings): MailForm {
  return {
    smtp_host: s.smtp_host,
    smtp_port: String(s.smtp_port),
    smtp_security: s.smtp_security,
    smtp_user: s.smtp_user ?? "",
    smtp_password: { kind: "keep" },
    from_name: s.from_name,
    from_address: s.from_address,
    reply_to: s.reply_to ?? "",
    envelope_from: s.envelope_from ?? "",
    sales_address: s.sales_address ?? "",
    bounce_imap_host: s.bounce_imap_host ?? "",
    bounce_imap_port: String(s.bounce_imap_port ?? 993),
    bounce_imap_user: s.bounce_imap_user ?? "",
    bounce_imap_password: { kind: "keep" },
  };
}

/** Absent keeps the stored password, null clears it, a string replaces it (the contract's writeOnly rule). */
function passwordField(p: PasswordEdit): { value?: string | null } {
  if (p.kind === "set") return { value: p.value };
  if (p.kind === "clear") return { value: null };
  return {};
}

/** The request body for save, test and check: optional fields left empty are left out. */
export function inputFrom(f: MailForm): MailSettingsInput {
  const opt = (v: string) => (v.trim() === "" ? undefined : v.trim());
  const body: MailSettingsInput = {
    smtp_host: f.smtp_host.trim(),
    smtp_port: Number(f.smtp_port),
    smtp_security: f.smtp_security,
    smtp_user: opt(f.smtp_user),
    from_name: f.from_name.trim(),
    from_address: f.from_address.trim(),
    reply_to: opt(f.reply_to),
    envelope_from: opt(f.envelope_from),
    sales_address: opt(f.sales_address),
    bounce_imap_host: opt(f.bounce_imap_host),
    bounce_imap_port: Number(f.bounce_imap_port) || 993,
    bounce_imap_user: opt(f.bounce_imap_user),
  };
  const smtp = passwordField(f.smtp_password);
  if ("value" in smtp) body.smtp_password = smtp.value;
  const bounce = passwordField(f.bounce_imap_password);
  if ("value" in bounce) body.bounce_imap_password = bounce.value;
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined)) as MailSettingsInput;
}

/** What the form would be refused for before it is sent; null when it may go (ML-01: no credentials in the clear). */
export function formProblem(f: MailForm): "host" | "port" | "from" | "plainWithUser" | null {
  if (f.smtp_host.trim() === "") return "host";
  const port = Number(f.smtp_port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) return "port";
  if (f.from_name.trim() === "" || f.from_address.trim() === "") return "from";
  if (f.smtp_security === "none" && f.smtp_user.trim() !== "") return "plainWithUser";
  return null;
}

/** A refused call in words: a missing capability, not built yet, the API's own detail. */
export function mailError(e: unknown, t: (k: string) => string): string {
  if (!(e instanceof ApiError)) return t("mailSettings.error.unknown");
  if (e.status === 403) return t("mailSettings.error.forbidden");
  if (e.status === 501) return t("mailSettings.error.notYet");
  if (e.code === "password_unreadable") return t("mailSettings.error.passwordUnreadable");
  return e.message;
}

export const mailSettingsKey = ["ops", "settings", "mail"] as const;

export function getMailSettings(): Promise<MailSettings> {
  return unwrap(apiClient("ops").GET("/v1/ops/settings/mail"));
}

export function putMailSettings(body: MailSettingsInput): Promise<MailSettings> {
  return unwrap(apiClient("ops").PUT("/v1/ops/settings/mail", { body }));
}

export function testMailSettings(body: MailSettingsInput, to: string): Promise<MailTestResult> {
  return unwrap(apiClient("ops").POST("/v1/ops/settings/mail/test", { body: { ...body, to } }));
}

export function checkBounceMailbox(body: MailSettingsInput): Promise<MailTestResult> {
  return unwrap(apiClient("ops").POST("/v1/ops/settings/mail/check-bounce", { body }));
}

// Allowed recipients (VisionPlatform notifications-email ML-85 to ML-87, P-250) over getMailAllowlist,
// putMailAllowlist and resetMailAllowlist: the dev or staging server's recipient allowlist.
export type MailAllowlist = components["schemas"]["MailAllowlist"];

/** The most entries the server takes (ML-86). */
export const maxAllowlistEntries = 200;

/**
 * One typed entry, lower-cased, or why it is refused (ML-86): an exact address, or "@" and a domain with a dot.
 * The server checks the same rules; this only says it sooner.
 */
export function cleanEntry(raw: string): { entry: string } | { problem: "address" | "domain" } {
  const e = raw.trim().toLowerCase();
  if (e.startsWith("@")) {
    const d = e.slice(1);
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) ? { entry: e } : { problem: "domain" };
  }
  return /^[^\s@<>(),;:"]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(e) ? { entry: e } : { problem: "address" };
}

/** The entries with one added, kept sorted and without duplicates. */
export function addEntry(entries: readonly string[], entry: string): string[] {
  return [...new Set([...entries, entry])].sort();
}

/** Whether the edited list differs from the one in force. */
export function entriesChanged(a: readonly string[], b: readonly string[]): boolean {
  return a.length !== b.length || a.some((e, i) => e !== b[i]);
}

/** A refused allowlist call in words: production, the API's detail, or the mail settings' general errors. */
export function allowlistError(e: unknown, t: (k: string) => string): string {
  if (e instanceof ApiError && e.status === 409) return t("mailAllowlist.error.production");
  return mailError(e, t);
}

export const mailAllowlistKey = ["ops", "settings", "mail", "allowlist"] as const;

export function getMailAllowlist(): Promise<MailAllowlist> {
  return unwrap(apiClient("ops").GET("/v1/ops/settings/mail/allowlist"));
}

export function putMailAllowlist(entries: string[]): Promise<MailAllowlist> {
  return unwrap(apiClient("ops").PUT("/v1/ops/settings/mail/allowlist", { body: { entries } }));
}

export function resetMailAllowlist(): Promise<MailAllowlist> {
  return unwrap(apiClient("ops").DELETE("/v1/ops/settings/mail/allowlist"));
}
